import { capSQLiteSet } from "@capacitor-community/sqlite";
import bbox from "@turf/bbox";
import EventEmitter from "eventemitter3";
import { BBox } from "geojson";
import { CoreoApp, CoreoAppSummary, CoreoAssociateMap, CoreoProjectFeatures, CoreoAttribute, CoreoAttributeType, CoreoCollectionItem, CoreoGeometry, CoreoRecord, CoreoRecordAttachment, CoreoRecordFilter, CoreoRecordSyncStatus, CoreoUser } from "../../types";
import { CoreoAPI } from "../api.service";
import { imageToImageProxy } from "../image.service";
import { clearRecordSyncError, setRecordSyncError } from "../record-sync-error.service";
import { isRecordUploadSuccessResponse, normaliseSyncError } from "../sync-error.service";
import {
  recomputeTreeSyncErrorsForAncestors,
  recomputeTreeSyncErrorsForRecord
} from "./tree-sync-error.service";
import { DB_SAFE_RANGE, fetchAttachmentMetadata, isNewRecord, uploadAttachment, uploadAttachmentClone } from "../records.service";
import Sentry from "../sentry.service";
import { AppSyncResource, SYNC_REQUEST_BATCH_SIZE, SYNC_WRITE_SQL_BUFFER_SIZE, SyncItem, SyncProject, SyncRecord, SyncUser } from "./app-sync.service";
import { FileSystemService, storeProjectIcon, writeProjectFile } from "./filesystem.service";
import { AppDatabaseAssociation, AppDatabaseAssociationRepo } from "./models/association.db";
import { AppDatabaseAttachment, AppDatabaseAttachmentRepo } from "./models/attachment.db";
import { AppDatabaseAttributeRepo } from "./models/attribute.db";
import { AppDatabaseTable } from "./models/base.db";
import { AppDatabaseCollectionRepo } from "./models/collection.db";
import { AppDatabaseFormRepo } from "./models/form.db";
import { AppDatabaseFormGroupRepo } from "./models/formGroup.db";
import { AppDatabaseItemRepo } from "./models/item.db";
import { AppDatabaseMapLayerRepo } from "./models/map-layer.db";
import { AppDatabaseMediaItemRepo } from "./models/media-item.db";
import { AppDatabasePageRepo } from "./models/page.db";
import { AppDatabaseProjectRepo } from "./models/project.db";
import { AppDatabaseRecord, AppDatabaseRecordRepo } from "./models/record.db";
import { AppDatabaseStateRepo } from "./models/state.db";
import { AppDatabaseUserRepo } from "./models/user.db";
import { compareProjectFeatures, ProjectFeaturesChange } from "./project-features";
import SQLDatabase, { SQLSelect, Transaction, TransactionQueryInterface } from "./sql.service";

export enum AppDatabaseSyncError {
  'FREE_TRIAL_EXPIRED',
  'ACCESS_DENIED',
  'UPLOAD_ERROR',
  'USER_ABORTED',
  'UNKNOWN'
};

type AppDatabaseEventTypes = {
  syncStart: (id: number, installation: boolean, showIssuesLink?: boolean) => void;
  syncProgress: (step: ProjectSyncStep, status: ProjectSyncStepState, progress?: number) => void;
  syncDidUpdate: (id: number, records: boolean, config: boolean, uploadError?: boolean) => void;
  syncComplete: (id: number, app: CoreoAppSummary) => void;
  syncError: (e: ProjectSyncError) => void;
  syncRecordIdUpdate: (oldId: number, newId: number) => void;
  projectFeaturesUpdated: (id: number, features: CoreoProjectFeatures) => void;
}


export class ProjectFreeTrialExpiredError extends Error {
  constructor() {
    super();
    this.name = 'ProjectFreeTrialExpiredError';
  }
}

export class ProjectAccessDeniedError extends Error {
  constructor() {
    super();
    this.name = 'ProjectAccessDeniedError';
  }
}

export class ProjectUnknownError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ProjectUnknownError';
  }
}

export type ProjectUploadFailureCause = 'validation' | 'network' | 'attachment' | 'server';

export class ProjectUploadError extends Error {
  readonly cause: ProjectUploadFailureCause;

  constructor(message: string, cause: ProjectUploadFailureCause) {
    super(message);
    this.name = 'ProjectUploadError';
    this.cause = cause;
  }
}

export type LoadRecordDepth = 'shallow' | 'full';

export interface LoadRecordOptions {
  depth?: LoadRecordDepth;
}

/** Download transaction was committed; failure happened afterward (e.g. upload). SQLite matches server for downloaded data. */
export class ProjectSyncPostCommitError extends Error {
  readonly cause: unknown;

  constructor(
    public readonly projectId: number,
    cause: unknown
  ) {
    super(
      cause instanceof Error ? cause.message : 'Sync failed after download was committed'
    );
    this.name = 'ProjectSyncPostCommitError';
    this.cause = cause;
  }
}

export class ProjectUserAbortError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ProjectUserAbortError';
  }
}

export class ProjectNotFoundError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ProjectNotFoundError';
  }
}

export class ProjectDeletedError extends Error {
  constructor(public date: string) {
    super();
    this.name = 'ProjectDeletedError';
  }
}

export type ProjectSyncError = |
  ProjectAccessDeniedError |
  ProjectFreeTrialExpiredError |
  ProjectUnknownError |
  ProjectNotFoundError |
  ProjectDeletedError |
  ProjectUserAbortError;


export enum ProjectSyncStep {
  CONFIG,
  USERS,
  RECORDS,
  UPLOAD
};

export enum ProjectSyncStepState {
  NOT_STARTED,
  RUNNING,
  COMPLETE,
  ERROR,
  CANCELLED
};

export interface ProjectSyncState {
  config: ProjectSyncStepState;
  users: ProjectSyncStepState;
  records: ProjectSyncStepState;
  upload: ProjectSyncStepState;
}

interface ProjectSyncCheckResponse {
  project: number;
  records: number;
  items: number;
  itemsLastUpdated: number;
  /** The plan's current features. Missing from older APIs. */
  features?: CoreoProjectFeatures;
}

interface ProjectSyncOptions {
  force?: boolean;
  step?: ProjectSyncStep;
  showIssuesLink?: boolean;
}

const parseMultiSelect = (json: string) => {
  try {
    const result = JSON.parse(json);
    if (Array.isArray(result)) {
      return result;
    }
    return json.split(',');
  } catch (e) {
    return json.split(',');
  }
}

export default class AppDatabase {

  // A map of attributes by id
  private attributeMap: Map<number, CoreoAttribute> = new Map<number, CoreoAttribute>();
  private attributeTypeMap: Map<string, CoreoAttributeType> = new Map<string, CoreoAttributeType>();

  // Stores a list of attribute paths for each form - used to build the insert data
  public formDataColumnMap: Map<number, string[]> = new Map<number, string[]>();

  private usersById = new Map<number, CoreoUser>();

  public static readonly DB_SAFE_RANGE = DB_SAFE_RANGE;

  public static instance: AppDatabase = new AppDatabase();
  public events: EventEmitter<AppDatabaseEventTypes> = new EventEmitter<AppDatabaseEventTypes>();

  public records: AppDatabaseRecordRepo = new AppDatabaseRecordRepo();
  public attachments: AppDatabaseAttachmentRepo = new AppDatabaseAttachmentRepo();
  public items: AppDatabaseItemRepo = new AppDatabaseItemRepo();
  public users: AppDatabaseUserRepo = new AppDatabaseUserRepo();
  public associations: AppDatabaseAssociationRepo = new AppDatabaseAssociationRepo();
  public projects: AppDatabaseProjectRepo = new AppDatabaseProjectRepo();
  public mediaItems: AppDatabaseMediaItemRepo = new AppDatabaseMediaItemRepo();
  public pages: AppDatabasePageRepo = new AppDatabasePageRepo();
  public attributes: AppDatabaseAttributeRepo = new AppDatabaseAttributeRepo();
  public collections: AppDatabaseCollectionRepo = new AppDatabaseCollectionRepo();
  public forms: AppDatabaseFormRepo = new AppDatabaseFormRepo();
  public formGroups: AppDatabaseFormGroupRepo = new AppDatabaseFormGroupRepo();
  public states: AppDatabaseStateRepo = new AppDatabaseStateRepo();
  public mapLayers: AppDatabaseMapLayerRepo = new AppDatabaseMapLayerRepo();

  private itemCache: Map<number, Map<string, CoreoCollectionItem>> = new Map<number, Map<string, CoreoCollectionItem>>();

  private syncAbortController: AbortController;

  private filterStandardColumns(attributes: CoreoAttribute[]) {
    return attributes.filter(attribute => {
      if (!attribute.surveyId) {
        return false;
      }
      if (['attachment'].includes(attribute.type)) {
        return false;
      }
      if (['geometry', 'text', 'child', 'association'].includes(attribute.questionType)) {
        return false;
      }
      return true;
    });
  }

  public async init(): Promise<void> {
    await this.createTables();
  }

  private async createTables() {
    const result = await Promise.all([
      this.projects.initTable(),
      this.pages.initTable(),
      this.attributes.initTable(),
      this.collections.initTable(),
      this.forms.initTable(),
      this.formGroups.initTable(),
      this.states.initTable(),
      this.mapLayers.initTable(),
      this.records.initTable(),
      this.attachments.initTable(),
      this.associations.initTable(),
      this.users.initTable(),
      this.items.initTable(),
      this.mediaItems.initTable(),
    ]);
    const operations = await result.flat();
    if (operations.length > 0) {
      await SQLDatabase.instance.executeSet(operations);
    }
  }

  private isPendingDeleteNewChild(associate: CoreoRecord, attributeId: number): boolean {
    const associationAttribute = this.attributeMap.get(attributeId);
    return (
      associationAttribute?.questionType === 'child' &&
      associate.syncState === CoreoRecordSyncStatus.PENDING_DELETE &&
      isNewRecord(associate)
    );
  }

  private tryWriteBackRecordId(record: CoreoRecord, id: number): void {
    if (record.id !== null) {
      return;
    }
    try {
      record.id = id;
    } catch {
      // Redux and other frozen in-memory trees cannot be mutated; DB persistence still uses saveIdMap.
    }
  }

  private resolvedSaveRecordId(
    record: CoreoRecord,
    saveIdMap: WeakMap<CoreoRecord, number>
  ): number | null {
    if (record.id !== null) {
      return record.id;
    }
    return saveIdMap.get(record) ?? null;
  }

  private assignSaveRecordIds(
    record: CoreoRecord,
    nextIdRef: { value: number },
    saveIdMap: WeakMap<CoreoRecord, number>
  ): void {
    if (saveIdMap.has(record)) {
      return;
    }
    if (record.id === null) {
      const id = nextIdRef.value;
      nextIdRef.value += 1;
      saveIdMap.set(record, id);
      this.tryWriteBackRecordId(record, id);
    } else {
      saveIdMap.set(record, record.id);
    }

    for (const attributeId in record.associates) {
      const associationAttribute = this.attributeMap.get(+attributeId);
      if (!associationAttribute) {
        continue;
      }

      for (const associate of record.associates[attributeId] ?? []) {
        if (this.isPendingDeleteNewChild(associate, +attributeId)) {
          continue;
        }
        if (associationAttribute.questionType === 'child') {
          this.assignSaveRecordIds(associate, nextIdRef, saveIdMap);
        }
      }
    }
  }

  private async collectPendingDeleteSaveOperations(
    record: CoreoRecord,
    operations: capSQLiteSet[]
  ): Promise<void> {
    for (const attributeId in record.associates) {
      const associationAttribute = this.attributeMap.get(+attributeId);
      if (!associationAttribute) {
        continue;
      }

      for (const associate of record.associates[attributeId] ?? []) {
        if (this.isPendingDeleteNewChild(associate, +attributeId)) {
          operations.push(...await this.getDeletePendingRecordOperations(associate));
          continue;
        }
        if (associationAttribute.questionType === 'child') {
          await this.collectPendingDeleteSaveOperations(associate, operations);
        }
      }
    }
  }

  private collectSaveTreeRecords(
    record: CoreoRecord,
    output: CoreoRecord[],
    seen: Set<number>,
    saveIdMap: WeakMap<CoreoRecord, number>
  ): void {
    const id = this.resolvedSaveRecordId(record, saveIdMap);
    if (id === null || seen.has(id)) {
      return;
    }
    seen.add(id);
    output.push(record);

    for (const attributeId in record.associates) {
      const associationAttribute = this.attributeMap.get(+attributeId);
      if (associationAttribute?.questionType !== 'child') {
        continue;
      }
      for (const associate of record.associates[attributeId] ?? []) {
        if (this.isPendingDeleteNewChild(associate, +attributeId)) {
          continue;
        }
        this.collectSaveTreeRecords(associate, output, seen, saveIdMap);
      }
    }
  }

  private collectSaveAssociationOperations(
    record: CoreoRecord,
    projectId: number,
    operations: capSQLiteSet[],
    associatesStatement: string,
    saveIdMap: WeakMap<CoreoRecord, number>
  ): void {
    const sourceId = this.resolvedSaveRecordId(record, saveIdMap);
    if (sourceId === null) {
      return;
    }

    for (const attributeId in record.associates) {
      const associationAttribute = this.attributeMap.get(+attributeId);
      if (!associationAttribute) {
        continue;
      }

      for (const associate of record.associates[attributeId] ?? []) {
        if (this.isPendingDeleteNewChild(associate, +attributeId)) {
          continue;
        }

        const associateRecordId = this.resolvedSaveRecordId(associate, saveIdMap);
        if (associateRecordId === null) {
          throw new Error(
            `saveRecord: association target has no id (attributeId=${attributeId}, surveyId=${associate.surveyId})`
          );
        }
        operations.push({
          statement: associatesStatement,
          values: [
            sourceId,
            associateRecordId,
            +attributeId,
            projectId
          ]
        });

        if (associationAttribute.questionType === 'child') {
          this.collectSaveAssociationOperations(associate, projectId, operations, associatesStatement, saveIdMap);
        }
      }
    }
  }

  public async saveRecord(parentRecord: CoreoRecord): Promise<void> {
    const associatesStatement = `INSERT OR REPLACE INTO associations
    (sourceId,targetId,attributeId,projectId) VALUES (?,?,?,?)`;

    const now = new Date();
    const timestamp = now.valueOf();
    const updatedAt = now.toISOString();
    const projectId = parentRecord.projectId;

    const transaction = await SQLDatabase.instance.transaction();

    try {
      const maxIdQuery = this.selectQueryBuilder('records')
        .field('MAX(id)', 'id')
        .where('id < ?', AppDatabase.DB_SAFE_RANGE);
      const maxIdResult = await transaction.query<{ id: number }>({
        statement: maxIdQuery.toString(),
        values: []
      });
      const nextIdRef = {
        value: (maxIdResult.length > 0 && maxIdResult[0].id != null)
          ? maxIdResult[0].id + 1
          : 1
      };

      const saveIdMap = new WeakMap<CoreoRecord, number>();
      this.assignSaveRecordIds(parentRecord, nextIdRef, saveIdMap);

      const operations: capSQLiteSet[] = [];
      await this.collectPendingDeleteSaveOperations(parentRecord, operations);

      const treeRecords: CoreoRecord[] = [];
      this.collectSaveTreeRecords(parentRecord, treeRecords, new Set(), saveIdMap);

      const treeIds = treeRecords
        .map(record => this.resolvedSaveRecordId(record, saveIdMap))
        .filter((id): id is number => id !== null);
      if (treeIds.length > 0) {
        operations.push({
          statement: this.deleteQueryBuilder('attachments')
            .where('recordId IN ?', treeIds)
            .toString(),
          values: []
        });
        operations.push({
          statement: this.deleteQueryBuilder('associations')
            .where('sourceId IN ?', treeIds)
            .toString(),
          values: []
        });
      }

      for (const record of treeRecords) {
        const recordId = this.resolvedSaveRecordId(record, saveIdMap);
        if (recordId === null) {
          continue;
        }
        operations.push(...this.records.toInsertSet({
          ...record,
          id: recordId,
          timestamp,
          userId: parentRecord.userId,
          state: parentRecord.state,
          createdAt: parentRecord.createdAt,
          updatedAt
        }));

        for (const attachment of record.attachments ?? []) {
          operations.push(...this.attachments.toInsertSet({
            ...attachment,
            recordId,
            projectId,
            size: attachment.size
          }));
        }
      }

      this.collectSaveAssociationOperations(
        parentRecord,
        projectId,
        operations,
        associatesStatement,
        saveIdMap
      );

      await transaction.executeSet(operations, SYNC_WRITE_SQL_BUFFER_SIZE);
      await transaction.commit();

      const savedRootId = this.resolvedSaveRecordId(parentRecord, saveIdMap);
      if (savedRootId !== null) {
        await recomputeTreeSyncErrorsForRecord(savedRootId);
      }
      this.events.emit('syncDidUpdate', projectId, true, false);
    } catch (e) {
      console.warn(e);
      Sentry.captureException(e, {
        extra: {
          record: parentRecord
        }
      });
      await transaction.rollback();
    }
  }

  public async getRecordTreeIds(current: CoreoRecord): Promise<number[]> {
    const ids = new Set<number>();
    await this._collectRecordTreeIds(current, ids, { traverseSoftDeletedChildren: false });
    return Array.from(ids);
  }

  /**
   * Same graph walk as {@link getRecordTreeIds}, but follows rows even when {@link deletedAt} is set
   * so pending-delete subtrees can be restored together with the root.
   */
  public async getRecordTreeIdsForRestore(current: CoreoRecord): Promise<number[]> {
    const ids = new Set<number>();
    await this._collectRecordTreeIds(current, ids, { traverseSoftDeletedChildren: true });
    return Array.from(ids);
  }

  private async _collectRecordTreeIds(
    current: CoreoRecord,
    ids: Set<number>,
    options: { traverseSoftDeletedChildren: boolean },
  ): Promise<void> {
    if (ids.has(current.id)) {
      return;
    }
    ids.add(current.id);

    const childAttributes: CoreoAttribute[] = await SQLDatabase.instance.query(
      'SELECT id FROM attributes WHERE surveyId = ? AND questionType = ?',
      [current.surveyId, 'child'],
    );
    for (const childAttribute of childAttributes) {
      const associations: AppDatabaseAssociation[] = await SQLDatabase.instance.query(
        'SELECT * FROM associations WHERE sourceId = ? AND attributeId = ?',
        [current.id, childAttribute.id],
      );
      for (const association of associations) {
        const liveOnlyClause = options.traverseSoftDeletedChildren ? '' : ' AND deletedAt IS NULL';
        const childRecord = await SQLDatabase.instance.query<AppDatabaseRecord>(
          `SELECT * FROM records WHERE id = ?${liveOnlyClause}`,
          [association.targetId],
        );
        if (childRecord && childRecord.length > 0) {
          await this._collectRecordTreeIds(await this.records.deserialise(childRecord[0]), ids, options);
        }
      }
    }
  }

  public async prepareRecordForDelete(record: CoreoRecord): Promise<void> {
    const now = new Date();
    const ids = await this.getRecordTreeIds(record);

    const deleteStatement = `UPDATE records SET deletedAt = ?, origSyncState = syncState, syncState = ? WHERE id IN (${ids.join(',')}) AND deletedAt IS NULL`;
    const deleteValues = [
      now.toISOString(),
      CoreoRecordSyncStatus.PENDING_DELETE
    ];

    await SQLDatabase.instance.executeSet([{
      statement: deleteStatement,
      values: deleteValues
    }]);
    this.events.emit('syncDidUpdate', record.projectId, true, false);
  }

  public async restoreRecord(record: CoreoRecord): Promise<void> {
    const ids = await this.getRecordTreeIdsForRestore(record);

    const restoreStatement = `UPDATE records SET deletedAt = NULL, syncState = origSyncState, origSyncState = NULL WHERE id IN (${ids.join(',')})`;

    await SQLDatabase.instance.executeSet([{
      statement: restoreStatement,
      values: []
    }]);
    this.events.emit('syncDidUpdate', record.projectId, true, false);
  }

  public deleteRecordSet(id: number): capSQLiteSet[] {
    return [
      {
        statement: this.deleteQueryBuilder('records').where('id = ?', id).toString(),
        values: []
      },
      {
        statement: this.deleteQueryBuilder('attachments').where('recordId = ?', id).toString(),
        values: []
      },
      {
        statement: this.deleteQueryBuilder('associations').where(
          SQLDatabase.expr().and('sourceId = ?', id).or('targetId = ?', id)).toString(),
        values: []
      }
    ]
  }

  public selectQueryBuilder(alias: AppDatabaseTable) {
    return SQLDatabase.select().from(alias);
  }

  public updateQueryBuilder(alias: AppDatabaseTable) {
    return SQLDatabase.update().table(alias);
  }

  public deleteQueryBuilder(alias: AppDatabaseTable) {
    return SQLDatabase.delete().from(alias);
  }

  async findAttachments(recordId: number): Promise<CoreoRecordAttachment[]> {
    const query = this.selectQueryBuilder('attachments')
      .where('recordId = ?', recordId);
    return await SQLDatabase.instance.query(query);
  }

  async findAssociates(recordId: number, direction: 'source' | 'target' = 'source'): Promise<CoreoAssociateMap> {
    const query = this.selectQueryBuilder('associations')
      .where(
        SQLDatabase.expr().and(direction === 'source' ? "sourceId = ?" : "targetId = ?", recordId)
      );
    const associations = await SQLDatabase.instance.query<AppDatabaseAssociation>(query);
    if (associations.length === 0) {
      return {};
    }

    const associateIds = [...new Set(associations.map((association) => {
      return association.targetId === recordId ? association.sourceId : association.targetId;
    }))];

    const rows = await SQLDatabase.instance.query<AppDatabaseRecord>(
      SQLDatabase.select().from('records').where('id IN ?', associateIds)
    );
    const recordById = new Map<number, CoreoRecord>();
    for (const row of rows) {
      recordById.set(row.id, this.records.deserialiseCore(row));
    }

    await this.applyRecordDataBatch(recordById);

    const result: CoreoAssociateMap = {};
    for (const association of associations) {
      const associateId = association.targetId === recordId ? association.sourceId : association.targetId;
      if (!result[association.attributeId]) {
        result[association.attributeId] = [];
      }
      const associateRecord = recordById.get(associateId);
      if (associateRecord) {
        result[association.attributeId].push(associateRecord);
      }
    }
    return result;
  }

  private async applyRecordDataBatch(recordsById: Map<number, CoreoRecord>): Promise<void> {
    const byFormId = new Map<number, number[]>();
    for (const record of recordsById.values()) {
      const formId = record.formId ?? record.surveyId;
      if (!formId) {
        continue;
      }
      const ids = byFormId.get(formId) ?? [];
      ids.push(record.id);
      byFormId.set(formId, ids);
    }

    for (const [formId, ids] of byFormId) {
      const dataRows = await SQLDatabase.instance.query<any>(
        SQLDatabase.select().from(`records_${formId}`).where('id IN ?', ids)
      );
      for (const row of dataRows) {
        const { id, ...raw } = row;
        const record = recordsById.get(id);
        if (record) {
          record.data = await this.coerceRecordData(raw);
        }
      }
    }
  }

  private async coerceRecordData(raw: Record<string, any>): Promise<CoreoRecord['data']> {
    const data = Object.fromEntries<any>(Object.entries<any>(raw).filter(([_, v]) => v !== null));

    for (const d in data) {
      const type = this.attributeTypeMap.get(d);
      switch (type) {
        case 'multiselect': {
          data[d] = data[d]?.length > 0 ? parseMultiSelect(data[d]) : [];
          break;
        }
        case 'boolean': {
          data[d] = Boolean(parseInt(data[d]));
          break;
        }
        case 'integer': {
          data[d] = parseInt(data[d]);
          break;
        }
        case 'float': {
          data[d] = parseFloat(data[d]);
          break;
        }
      }
    }
    return data;
  }

  private async resolveRecordUser(
    userId: number,
    usersById: Map<number, CoreoUser>
  ): Promise<CoreoUser | null> {
    const sessionCached = this.usersById.get(userId);
    if (sessionCached) {
      usersById.set(userId, sessionCached);
      return sessionCached;
    }
    const cached = usersById.get(userId);
    if (cached) {
      return cached;
    }
    const user = await this.users.findById(userId);
    if (user) {
      this.usersById.set(userId, user);
      usersById.set(userId, user);
    }
    return user;
  }

  async updateAttachmentUrl(attachmentId: number, url: string) {
    const query = this.updateQueryBuilder('attachments')
      .where('id = ?', attachmentId)
      .set('url', url);
    return SQLDatabase.instance.query(query);
  }

  async updateAttachmentProperties(attachmentId: number, size: number, mimeType: string) {
    const query = this.updateQueryBuilder('attachments')
      .where('id = ?', attachmentId)
      .set('size', size)
      .set('mimeType', mimeType);
    return SQLDatabase.instance.query(query);
  }

  private async loadRecordData(id: number, formId: number) {
    const raw = await this.records.getRecordData(id, formId);
    return this.coerceRecordData(raw);
  }

  private async findRecordRowById(id: number): Promise<AppDatabaseRecord | null> {
    const result = await SQLDatabase.instance.query<AppDatabaseRecord>(
      SQLDatabase.select().from('records').where('id = ?', id).limit(1)
    );
    if (result.length === 0) {
      return null;
    }
    return result[0];
  }

  private async hydrateRecord(id: number): Promise<CoreoRecord | null> {
    const row = await this.findRecordRowById(id);
    if (!row) {
      return null;
    }
    const record = this.records.deserialiseCore(row);
    record.data = await this.loadRecordData(id, record.formId);
    return record;
  }

  async loadRecord(id: number, options: LoadRecordOptions = {}): Promise<CoreoRecord | null> {
    const depth = options.depth ?? 'full';
    const usersById = new Map<number, CoreoUser>();
    if (depth === 'full') {
      return this.loadRecordFullBatched(id, usersById);
    }
    return this.loadRecordInternal(id, usersById);
  }

  /** Explicit alias for {@link loadRecord} with default full depth (sync/tests). */
  loadRecordFull(id: number): Promise<CoreoRecord | null> {
    return this.loadRecord(id, { depth: 'full' });
  }

  private async collectTreeIdsAndAssociations(rootId: number): Promise<{
    allIds: Set<number>;
    associationsBySourceId: Map<number, AppDatabaseAssociation[]>;
  }> {
    const allIds = new Set<number>([rootId]);
    const associationsBySourceId = new Map<number, AppDatabaseAssociation[]>();
    const queued: number[] = [rootId];
    const enqueued = new Set<number>([rootId]);

    while (queued.length > 0) {
      const batch = queued.splice(0, queued.length);
      const rows = await SQLDatabase.instance.query<AppDatabaseAssociation>(
        this.selectQueryBuilder('associations').where('sourceId IN ?', batch)
      );
      for (const row of rows) {
        const sourceRows = associationsBySourceId.get(row.sourceId) ?? [];
        sourceRows.push(row);
        associationsBySourceId.set(row.sourceId, sourceRows);
        allIds.add(row.targetId);
        if (!enqueued.has(row.targetId)) {
          enqueued.add(row.targetId);
          queued.push(row.targetId);
        }
      }
    }

    return { allIds, associationsBySourceId };
  }

  private assembleRecordTree(
    id: number,
    recordById: Map<number, CoreoRecord>,
    associationsBySourceId: Map<number, AppDatabaseAssociation[]>,
    attachmentsByRecordId: Map<number, CoreoRecordAttachment[]>,
    visited: Set<number>
  ): CoreoRecord | null {
    const record = recordById.get(id);
    if (!record) {
      return null;
    }

    if (visited.has(id)) {
      console.warn('cycle detected', id);
      return {
        ...record,
        associates: {},
        attachments: attachmentsByRecordId.get(id) ?? []
      };
    }
    visited.add(id);

    record.attachments = attachmentsByRecordId.get(id) ?? [];
    const associationRows = associationsBySourceId.get(id) ?? [];
    const associates: CoreoAssociateMap = {};

    for (const row of associationRows) {
      if (!associates[row.attributeId]) {
        associates[row.attributeId] = [];
      }
      const child = this.assembleRecordTree(
        row.targetId,
        recordById,
        associationsBySourceId,
        attachmentsByRecordId,
        visited
      );
      if (!child) {
        continue;
      }
      associates[row.attributeId].push(child);
    }

    record.associates = associates;
    return record;
  }

  private async loadRecordFullBatched(
    rootId: number,
    usersById: Map<number, CoreoUser>
  ): Promise<CoreoRecord | null> {
    const { allIds, associationsBySourceId } = await this.collectTreeIdsAndAssociations(rootId);
    const recordById = new Map<number, CoreoRecord>();

    const ids = [...allIds];
    if (ids.length > 0) {
      const rows = await SQLDatabase.instance.query<AppDatabaseRecord>(
        SQLDatabase.select().from('records').where('id IN ?', ids)
      );
      for (const row of rows) {
        recordById.set(row.id, this.records.deserialiseCore(row));
      }
    }

    if (!recordById.has(rootId)) {
      return null;
    }

    await this.applyRecordDataBatch(recordById);

    const attachmentRows = ids.length > 0
      ? await SQLDatabase.instance.query<AppDatabaseAttachment>(
        SQLDatabase.select().from('attachments').where('recordId IN ?', ids)
      )
      : [];

    const attachmentsByRecordId = new Map<number, CoreoRecordAttachment[]>();
    const deserialisedAttachments = await Promise.all(
      attachmentRows.map(attachment => this.attachments.deserialise(attachment))
    );
    for (const attachment of deserialisedAttachments) {
      const list = attachmentsByRecordId.get(attachment.recordId) ?? [];
      list.push(attachment);
      attachmentsByRecordId.set(attachment.recordId, list);
    }

    for (const record of recordById.values()) {
      if (record.userId) {
        record.user = await this.resolveRecordUser(record.userId, usersById);
      }
    }

    return this.assembleRecordTree(
      rootId,
      recordById,
      associationsBySourceId,
      attachmentsByRecordId,
      new Set()
    );
  }

  private async loadRecordInternal(
    id: number,
    usersById: Map<number, CoreoUser>,
  ): Promise<CoreoRecord | null> {
    const record = await this.hydrateRecord(id);
    if (!record) {
      return null;
    }

    const [attachments, user, associations] = await Promise.all([
      this.attachments.query({ recordId: id }),
      this.resolveRecordUser(record.userId, usersById),
      this.findAssociates(id)
    ]);
    record.attachments = attachments;
    record.user = user;
    record.associates = associations;
    return record;
  }

  public async getCollectionBounds(collectionId: number, projectId: number): Promise<BBox> {

    const features: CoreoGeometry[] = [];

    for await (const batch of this.items.search(q => q.where('collectionId = ?', collectionId).where('projectId = ?', projectId).where('geometry IS NOT NULL'), 1000)) {
      for (const item of batch) {
        features.push(item.geometry);
      }
    }

    return bbox({
      type: 'FeatureCollection',
      features: features.map(f => ({ type: 'Feature', properties: {}, geometry: f }))
    }) as BBox;
  }

  public async getCollectionItem(collectionId: number, projectId: number, key: string): Promise<CoreoCollectionItem> {

    const cached = this.itemCache.get(collectionId)?.get(key);
    if (cached) {
      return cached;
    }

    if (!this.itemCache.has(collectionId)) {
      this.itemCache.set(collectionId, new Map<string, CoreoCollectionItem>());
    }
    const item = await this.items.queryOne({ collectionId, projectId, key });

    this.itemCache.get(collectionId).set(key, item);
    return item;
  }

  private async getLastIdAndTimestamp(projectId: number, table: AppDatabaseTable, query: TransactionQueryInterface, filter: { [key: string]: any } = {}): Promise<[number, number]> {
    const lastQuery = this.selectQueryBuilder(table)
      .field('timestamp')
      .field('id')
      .limit(1)
      .where('projectId = ? ', projectId)
      .order('timestamp', false)
      .order('id', false);

    for (const f in filter) {
      lastQuery.where(`${f} = ?`, filter[f]);
    }

    const last = await query<{ id: number; timestamp: number }>({
      statement: lastQuery.toString(),
      values: []
    });

    if (last.length > 0) {
      return [last[0].id, last[0].timestamp];
    };
    return [0, 0];
  }

  private async getLastIdAndTimestampForRecordSyncFallback(
    projectId: number,
    query: TransactionQueryInterface
  ): Promise<[number, number]> {
    const serverKnownSyncStates = [
      CoreoRecordSyncStatus.SUBMITTED,
      CoreoRecordSyncStatus.PENDING_UPDATE,
      CoreoRecordSyncStatus.PENDING_DELETE
    ];
    const serverTimestampSyncStates = [
      CoreoRecordSyncStatus.SUBMITTED,
      CoreoRecordSyncStatus.PENDING_DELETE
    ];

    const idQuery = this.selectQueryBuilder('records')
      .field('id')
      .limit(1)
      .where('projectId = ?', projectId)
      .where('syncState IN ?', serverKnownSyncStates)
      .order('timestamp', false)
      .order('id', false);

    const timestampQuery = this.selectQueryBuilder('records')
      .field('timestamp')
      .limit(1)
      .where('projectId = ?', projectId)
      .where('syncState IN ?', serverTimestampSyncStates)
      .order('timestamp', false)
      .order('id', false);

    const [idResult, timestampResult] = await Promise.all([
      query<{ id: number }>({
        statement: idQuery.toString(),
        values: []
      }),
      query<{ timestamp: number }>({
        statement: timestampQuery.toString(),
        values: []
      })
    ]);

    if (idResult.length === 0) {
      return [0, 0];
    }

    const lastUpdatedAt = timestampResult.length > 0 ? timestampResult[0].timestamp : 0;
    return [idResult[0].id, lastUpdatedAt];
  }

  private async getLastRecordIdAndTimestamp(projectId: number, query: TransactionQueryInterface): Promise<[number, number]> {
    const queryObj = this.selectQueryBuilder('projects')
      .where('id = ?', projectId)
      .field('lastRecordId')
      .field('lastRecordTimestamp');

    const result = await query<{ lastRecordId: number; lastRecordTimestamp: number }>({
      statement: queryObj.toString(),
      values: []
    });

    if (result.length === 0) {
      return [null, null];
    }

    return [result[0].lastRecordId, result[0].lastRecordTimestamp];
  }

  public async getAllPendingUpdatesCount(): Promise<number> {
    const query = `SELECT COUNT(*) AS count FROM records WHERE syncState <> ?`;
    const result = await SQLDatabase.instance.query<{ count: number }>(query, [CoreoRecordSyncStatus.SUBMITTED]);
    return result[0].count;
  }

  public applyCoreoFilterToQuery(query: SQLSelect, filter: CoreoRecordFilter): SQLSelect {
    if (filter.userId) {
      query.where('userId = ?', filter.userId);
    }
    if (filter.forms.length > 0) {
      query.where('surveyId IN ?', filter.forms);
    }
    if (filter.states.length > 0) {
      query.where('state IN ?', filter.states);
    }
    if (filter.from) {
      query.where('createdAt >= ?', filter.from);
    }
    if (filter.to) {
      query.where('createdAt <= ?', filter.to);
    }
    return query;
  }

  public async pendingError(id: number, error: unknown, context: Record<string, unknown> = {}): Promise<void> {
    await setRecordSyncError(id, error);
    await recomputeTreeSyncErrorsForRecord(id);

    Sentry.withScope(scope => {
      scope.setContext("uploadError", {
        id,
        error,
        ...context
      });
      Sentry.captureMessage("Sync Record Error");
    });
  }

  public async resolvePendingUpdate(id: number, current: CoreoRecord): Promise<void> {
    const operations: capSQLiteSet[] = [];

    operations.push({
      statement: this.updateQueryBuilder('records')
        .where('id = ?', id)
        .set("id", current.id)
        .set("syncState", CoreoRecordSyncStatus.SUBMITTED)
        .set("syncError", null)
        .set("treeSyncError", null)
        .set("updatedAt", current.updatedAt)
        .set("timestamp", current.timestamp)
        .set("userId", current.userId)
        .toString(),
      values: []
    });

    const dataUpdate = SQLDatabase.update().table('records_' + current.surveyId)
      .set('id', current.id)
      .where('id = ?', id);

    for (const p in current.data) {
      const update = Array.isArray(current.data[p]) ? JSON.stringify((current.data[p] as any)) : current.data[p];
      dataUpdate.set(p, update);
    }
    const { text: statement, values } = dataUpdate.toParam();

    operations.push({
      statement,
      values
    });

    if (id !== current.id) {
      // Shouldn't have to do this?
      const associateUpdateA = this.updateQueryBuilder('associations')
        .set("sourceId", current.id)
        .where("sourceId = ?", id);
      operations.push({
        statement: associateUpdateA.toString(),
        values: []
      });

      const associateUpdateB = this.updateQueryBuilder('associations')
        .set("targetId", current.id)
        .where("targetId = ?", id);
      operations.push({
        statement: associateUpdateB.toString(),
        values: []
      });

      const associateUpdateC = this.updateQueryBuilder('attachments')
        .set("recordId", current.id)
        .where("recordId = ?", id);
      operations.push({
        statement: associateUpdateC.toString(),
        values: []
      });
    }

    operations.push({
      statement: 'UPDATE projects SET lastRecordId = ?, lastRecordTimestamp = ? WHERE id = ? AND (lastRecordId IS NULL OR lastRecordTimestamp IS NULL OR lastRecordId < ? OR lastRecordTimestamp < ?)',
      values: [
        current.id,
        current.timestamp,
        current.projectId,
        current.id,
        current.timestamp
      ]
    });

    await SQLDatabase.instance.executeSet(operations);
    await recomputeTreeSyncErrorsForRecord(current.id);
  }

  public async resolvePendingDelete(record: CoreoRecord): Promise<void> {
    const operations = this.deleteRecordSet(record.id);
    operations.push({
      statement: 'UPDATE projects SET lastRecordId = ?, lastRecordTimestamp = ? WHERE id = ? AND (lastRecordId IS NULL OR lastRecordTimestamp IS NULL OR lastRecordId < ? OR lastRecordTimestamp < ?)',
      values: [
        record.id,
        record.timestamp,
        record.projectId,
        record.id,
        record.timestamp
      ]
    });
    await SQLDatabase.instance.executeSet(operations);
    await recomputeTreeSyncErrorsForAncestors(record.id);
  }

  public async getDeletePendingRecordOperations(record: CoreoRecord): Promise<capSQLiteSet[]> {
    const operations = this.deleteRecordSet(record.id);
    const ids = await this.getRecordTreeIds(record);
    for (const treeId of ids) {
      operations.push(...this.deleteRecordSet(treeId));
    }
    return operations;
  }

  public async deletePendingRecord(record: CoreoRecord): Promise<void> {
    const operations = await this.getDeletePendingRecordOperations(record);
    await SQLDatabase.instance.executeSet(operations);
    await recomputeTreeSyncErrorsForAncestors(record.id);
    this.events.emit('syncDidUpdate', record.projectId, true, false);
  }

  public async getProjectFromAPI(id: number, signal?: AbortSignal): Promise<SyncProject> {
    const result = await CoreoAPI.instance.get(`/sync/${id}`, { authentication: true, signal });
    if (result.ok) {
      return await result.json() as SyncProject;
    } else if (result.status === 401) {
      throw new ProjectAccessDeniedError();
    }
    throw new ProjectUnknownError(result.statusText);
  };

  // Returns true if an update is required
  public async syncCheck(id: number, signal: AbortSignal, query: TransactionQueryInterface): Promise<ProjectSyncCheckResponse> {

    // Need to get lastId, lastUpdatedAt and the project rev
    // transaction ??= await SQLDatabase.instance.transaction();
    const [lastId, lastUpdatedAt] = await this.getRecordSyncPoint(id, query);
    const rev = await this.getProjectRev(id, query);

    const url = `/sync/${id}/check?lastId=${lastId}&lastUpdatedAt=${lastUpdatedAt}&rev=${rev}`;
    const response = await CoreoAPI.instance.get(url, {
      authentication: true,
      signal
    });

    if (!response.ok) {
      if (response.status === 410) {
        const d: { deletedAt: string } = await response.json();
        throw new ProjectDeletedError(d.deletedAt);
      }
      if (response.status === 404) {
        throw new ProjectNotFoundError('Project not found');
      }
      return null;
    }

    const result: ProjectSyncCheckResponse = await response.json();
    return result;
  }

  public async projectToSQLSet(project: SyncProject, transaction: Transaction): Promise<capSQLiteSet[]> {
    const set: capSQLiteSet[] = [];
    const projectId = project.id;

    set.push(...this.projects.toInsertSet(project));

    // Delete and re-add attributes
    set.push(...this.attributes.toDeleteSet(projectId));
    for (const attribute of project.attributes) {
      set.push(...this.attributes.toInsertSet({
        ...attribute,
        projectId
      }));
    }

    const formIds = project.surveys.map(s => s.id).join(',');
    const deletedForms = await transaction.query<{ id: number }>({
      statement: `SELECT id FROM forms WHERE projectId = ${projectId} AND id NOT IN (${formIds})`,
      values: []
    });

    for (const deletedForm of deletedForms ?? []) {
      set.push({
        statement: 'DELETE FROM records WHERE surveyId = ? AND projectId = ?',
        values: [deletedForm.id, project.id]
      }, {
        statement: 'DELETE FROM forms WHERE id = ? AND projectId = ?',
        values: [deletedForm.id, project.id]
      });
    }

    for (const form of project.surveys) {

      set.push(...this.forms.toInsertSet({
        ...form,
        parentProjectId: form.projectId !== projectId ? form.projectId : null,
        projectId
      }));
      const tableColumns = await SQLDatabase.instance.tableColumns(`records_${form.id}`);
      const formAttributes = project.attributes.filter(a => {
        if (a.surveyId !== form.id) {
          return false;
        }
        if (['attachment'].includes(a.type)) {
          return false;
        }
        if (['geometry', 'text', 'child', 'association'].includes(a.questionType)) {
          return false;
        }
        return true;
      });

      // If we are creating the table
      if (tableColumns.length === 0) {
        const attributes = formAttributes.length > 0 ? (', ' + formAttributes.map(a => `"${a.path}" TEXT`).join(',')) : '';
        set.push({
          statement: `CREATE TABLE IF NOT EXISTS records_${form.id} (id INTEGER PRIMARY KEY NOT NULL ${attributes});`,
          values: []
        });
      }
      else {
        // Add missing columns
        for (const formAttribute of formAttributes) {
          if (typeof tableColumns.find(a => a.name === formAttribute.path) === 'undefined') {
            set.push({
              statement: `ALTER TABLE "records_${form.id}" ADD COLUMN "${formAttribute.path}" TEXT`,
              values: []
            })
          }
        }

        // Now clear any columns that we shouldn't have
        // On Android (versions of sqlite < 3.35.0), we can't drop columns, so we have to set them to null
        // https://www.sqlite.org/changes.html
        for (const tableColumn of tableColumns.filter(t => t.name !== 'id')) {
          if (typeof formAttributes.find(f => f.path === tableColumn.name) === 'undefined') {
            set.push({
              statement: `UPDATE "records_${form.id}" SET "${tableColumn.name}" = NULL`,
              values: []
            });
          }
        }
      }
    }

    // Delete any attachments that should no longer be here
    const attachmentAttributes = project.attributes.filter(a => ['attachment', 'media'].includes(a.type));
    const attachmentDelete = attachmentAttributes.length === 0 ? '' : `AND attributeId NOT IN (${attachmentAttributes.map(a => a.id).join(',')})`;
    const attachmentDeleteStatement = `DELETE FROM attachments WHERE projectId = ? ${attachmentDelete}`;

    set.push({
      statement: attachmentDeleteStatement,
      values: [projectId]
    });

    // Delete any associations that should no longer be here
    const associationAttributes = project.attributes.filter(a => ['association', 'child'].includes(a.questionType));
    const associationDelete = associationAttributes.length === 0 ? '' : `AND attributeId NOT IN (${associationAttributes.map(a => a.id).join(',')})`;
    const associationDeleteStatement = `DELETE FROM associations WHERE projectId = ? ${associationDelete}`;

    set.push({
      statement: associationDeleteStatement,
      values: [projectId]
    });

    // Delete and re-add form groups
    set.push(...this.formGroups.toDeleteSet(projectId));
    for (const group of project.formGroups ?? []) {
      set.push(...this.formGroups.toInsertSet({ ...group, projectId }));
    }

    // Delete and re-add collections
    set.push(...this.collections.toDeleteSet(projectId));
    for (const collection of project.collections) {
      set.push(...this.collections.toInsertSet({
        ...collection,
        projectId
      }));
    }

    // Delete and re-add pages
    set.push(...this.pages.toDeleteSet(projectId));
    for (const page of project.pages) {
      set.push(...this.pages.toInsertSet({
        ...page,
        projectId
      }));

      for (const mediaItem of page.mediaItems) {
        set.push(...this.mediaItems.toInsertSet({
          ...mediaItem,
          projectId,
          itemId: null,
          pageId: page.id
        }));
      }
    }

    // Delete and re-add states
    set.push(...this.states.toDeleteSet(projectId));
    for (const state of project.states) {
      set.push(...this.states.toInsertSet({
        ...state,
        projectId
      }))
    }

    // Delete and re-add maps and map layers
    set.push(...this.mapLayers.toDeleteSet(projectId));

    for (const layer of project.maps) {
      set.push(...this.mapLayers.toInsertSet({
        ...layer,
        projectId
      }));
    }

    return set;
  };

  private async installProject(projectId: number, transaction: Transaction, signal: AbortSignal): Promise<CoreoApp> {

    let project: SyncProject = await this.getProjectFromAPI(projectId, signal);
    signal.throwIfAborted();

    if (project.freeTrialExpired) {
      throw new ProjectFreeTrialExpiredError();
    }

    let set = await this.projectToSQLSet(project, transaction);
    signal.throwIfAborted();

    await transaction.executeSet(set, SYNC_WRITE_SQL_BUFFER_SIZE);
    signal.throwIfAborted();

    const projectApp: CoreoApp = {
      id: project.id,
      name: project.name,
      description: project.description,
      forms: project.surveys.map(survey => ({
        ...survey,
        parentProjectId: survey.projectId !== project.id ? survey.projectId : null,
        projectId: project.id
      })),
      formGroups: project.formGroups,
      states: project.states,
      attributes: project.attributes,
      pages: project.pages,
      slug: project.slug,
      bounds: project.bounds,
      collections: project.collections,
      icon: project.icon,
      membership: project.membership,
      welcomePageId: project.welcomePageId,
      hideUsernames: project.hideUsernames,
      css: project.css,
      features: project.features,
      maps: project.maps
    };
    return projectApp;
  }

  private async cacheProjectConfig(app: CoreoApp) {
    const { forms, attributes } = app;


    this.formDataColumnMap.clear();
    for (const form of forms) {
      const formAttributes = this.filterStandardColumns(attributes.filter(a => a.surveyId === form.id))
        .filter(a => !!a.path)
        .map(a => a.path);
      this.formDataColumnMap.set(form.id, formAttributes);
    }

    this.attributeMap.clear();
    this.attributeTypeMap.clear();

    for (const attribute of attributes) {
      this.attributeMap.set(attribute.id, attribute);
      this.attributeTypeMap.set(attribute.path, attribute.type);
    }
  }

  public async loadProject(projectId: number): Promise<CoreoApp> {

    const results = await Promise.all([
      this.projects.findById(projectId),
      this.forms.query({ projectId }),
      this.formGroups.query({ projectId }),
      this.attributes.search({ projectId }, 10).toArray(),
      this.pages.query({ projectId }),
      this.collections.search({ projectId }, 10).toArray(),
      this.states.query({ projectId }),
      this.mapLayers.query({ projectId })
    ]);

    const [projectBase, forms, formGroups, attributes, pages, collections, states, maps] = results;

    if (projectBase === null) {
      throw Error('Project not found');
    }

    const project: CoreoApp = {
      ...projectBase,
      forms,
      formGroups,
      attributes,
      states,
      pages,
      collections,
      maps
    };

    this.itemCache.clear();
    this.usersById.clear();
    await this.cacheProjectConfig(project);

    return project;
  }

  private async getRecordSyncPoint(id: number, transactionQuery: TransactionQueryInterface): Promise<number[]> {
    const [lastId, lastUpdatedAt] = await this.getLastRecordIdAndTimestamp(id, transactionQuery);
    const hasStoredCursor = lastId != null && lastId > 0;
    if (hasStoredCursor) {
      return [lastId, lastUpdatedAt];
    }
    return this.getLastIdAndTimestampForRecordSyncFallback(id, transactionQuery);
  }

  /** Compare a sync check's features with the ones stored for the project. */
  private async checkProjectFeatures(id: number, features: CoreoProjectFeatures | undefined, query: TransactionQueryInterface): Promise<ProjectFeaturesChange> {
    if (!features) {
      return 'none';
    }
    const result = await query<{ features: string | null }>({
      statement: "SELECT features FROM projects WHERE id = ?",
      values: [id]
    });
    const stored = result[0]?.features;
    return compareProjectFeatures(stored ? JSON.parse(stored) : null, features);
  }

  private projectFeaturesSet(id: number, features: CoreoProjectFeatures): capSQLiteSet[] {
    return [{
      statement: "UPDATE projects SET features = ? WHERE id = ?",
      values: [JSON.stringify(features), id]
    }];
  }

  /**
   * Save new plan features in place, without a config sync, and tell listeners once committed.
   * The write runs in a transaction, so it waits for a running sync; `isCurrent` is checked
   * once the lock is held, so a check superseded by that sync is dropped.
   */
  public async applyProjectFeatures(id: number, features: CoreoProjectFeatures | undefined, isCurrent: () => boolean = () => true): Promise<ProjectFeaturesChange> {
    // Read-only first, so an unchanged poll doesn't open (and on web, persist) a transaction.
    const query: TransactionQueryInterface = (set: capSQLiteSet) => SQLDatabase.instance.querySet(set);
    if (await this.checkProjectFeatures(id, features, query) === 'none') {
      return 'none';
    }

    const transaction = await SQLDatabase.instance.transaction();
    let change: ProjectFeaturesChange = 'none';
    try {
      if (isCurrent()) {
        change = await this.checkProjectFeatures(id, features, transaction.query);
        if (change === 'patch') {
          await transaction.executeSet(this.projectFeaturesSet(id, features));
        }
      }
      await transaction.commit();
    } catch (e) {
      await transaction.rollback();
      throw e;
    }

    if (change === 'patch') {
      this.events.emit('projectFeaturesUpdated', id, features);
    }
    return change;
  }

  private async getProjectRev(id: number, query: TransactionQueryInterface): Promise<number> {
    const result = await query<{ rev: number }>({
      statement: "SELECT rev FROM projects WHERE id = ?",
      values: [id]
    });
    return result[0]?.rev ?? 0;
  }

  private attachmentMergeKey(attachment: CoreoRecordAttachment): string {
    if (attachment.url?.startsWith('http')) {
      return `${attachment.attributeId}:${attachment.url}`;
    }
    return `${attachment.attributeId}:pending:${attachment.fileLocation ?? attachment.id}`;
  }

  private findDownloadedAttachment(
    downloadedAttachments: AppDatabaseAttachment[],
    recordId: number,
    attachment: CoreoRecordAttachment
  ): AppDatabaseAttachment | undefined {
    if (attachment.url?.startsWith('http')) {
      return downloadedAttachments.find(a => a.recordId === recordId && a.url === attachment.url);
    }
    return downloadedAttachments.find(a => {
      return a.recordId === recordId && a.fileLocation === attachment.fileLocation;
    });
  }

  public async syncRecords(id: number, transaction: Transaction, signal: AbortSignal, force: boolean, skipDeletes: boolean = false): Promise<CoreoRecord[]> {

    const [lastId, lastUpdatedAt] = await this.getRecordSyncPoint(id, transaction.query);

    // First find all pending records
    const updates = await this.records.getPendingUpdateIds(id);

    const resource = new AppSyncResource<SyncRecord>(id, 'records', lastId ?? 0, lastUpdatedAt ?? 0, SYNC_REQUEST_BATCH_SIZE, force, signal);
    const operations: capSQLiteSet[] = [];
    const conflicts: CoreoRecord[] = [];

    let total: number;
    let lastRecord: SyncRecord;

    const flushBuffer = async (minBufferSize = 0) => {
      signal?.throwIfAborted();
      if (operations.length > 0 && (operations.length > minBufferSize)) {
        await transaction.executeSet(operations);
        operations.length = 0;
      }
    };

    const downloadedAttachments = force ? [] : await transaction.query<AppDatabaseAttachment>({
      statement: 'SELECT recordId, url, fileLocation FROM attachments WHERE projectId = ? AND fileLocation IS NOT NULL',
      values: [id]
    });

    for await (const batch of resource) {
      if (typeof total === 'undefined') {
        total = batch.result.length + batch.remaining;
      }
      this.events.emit('syncProgress', ProjectSyncStep.RECORDS, ProjectSyncStepState.RUNNING, (total - batch.remaining) / total);

      for (const record of batch.result) {
        // Update the last record pointer
        lastRecord = record;
        const isConflict = updates.includes(record.id);
        // If we have a conflict
        /**
         * Conflict resolution:
         * When both server and app have modified a record, merge associations and attachments from both sides.
         * Associations are merged by combining all unique targets for each attribute from both server and local records.
         * Attachments are merged by including all unique attachments from both server and local sources.
         * The app's pending state is preserved to ensure local changes are not lost.
         */
        if (isConflict) {
          conflicts.push(record);

          // First check if the server is trying to delete the record - if it is just delete it and move on
          if (record.deletedAt) {
            if (skipDeletes) {
              continue;
            }
            if (!force) {
              operations.push(...this.deleteRecordSet(record.id));
            }
            continue;
          }

          const localRecord = await this.loadRecord(record.id);
          if (!localRecord) {
            conflicts.push(record);
            continue;
          }

          // Build a merged associate map keyed by attribute id
          const mergedAssociates: Record<number, Set<number>> = {};
          // Start with the servers associates
          for (const association of record.sourceAssociations) {
            if (association.sourceId !== record.id) {
              continue;
            }
            mergedAssociates[association.attributeId] ||= new Set();
            mergedAssociates[association.attributeId].add(association.targetId);
          }
          // Add any new ones we have locally
          for (const attributeId in localRecord.associates) {
            const attr = Number(attributeId);
            mergedAssociates[attr] ||= new Set();
            for (const child of localRecord.associates[attributeId]) {
              if (child.id < DB_SAFE_RANGE) {
                mergedAssociates[attr].add(child.id);
              }
            }
          }

          // Rewrite associations for this parent using the merged set
          operations.push(...this.associations.toDeleteSetForRecord(record.id));
          for (const attributeId in mergedAssociates) {
            for (const targetId of mergedAssociates[attributeId]) {
              operations.push(...this.associations.toInsertSet([
                record.id,
                targetId,
                Number(attributeId),
                id
              ]));
            }
          }

          // Now merge attachments
          const mergedAttachments = new Map<string, CoreoRecordAttachment>();

          // Start with the servers attachments
          for (const attachment of record.attachments) {
            const key = this.attachmentMergeKey(attachment);
            mergedAttachments.set(key, attachment);
          }
          // Add local attachments
          const localAttachments = await this.attachments.query({ recordId: record.id });
          for (const attachment of localAttachments) {
            const key = this.attachmentMergeKey(attachment);
            if (!mergedAttachments.has(key)) {
              mergedAttachments.set(key, attachment);
            }
          }
          // Rewrite attachments
          operations.push(...this.attachments.toDeleteSetForRecord(record.id));
          for (const attachment of mergedAttachments.values()) {
            const existing = this.findDownloadedAttachment(downloadedAttachments, record.id, attachment);
            operations.push(...this.attachments.toInsertSet({
              url: attachment.url,
              mimeType: attachment.mimeType,
              recordId: record.id,
              projectId: id,
              attributeId: attachment.attributeId,
              size: attachment.size,
              fileLocation: existing?.fileLocation ?? attachment.fileLocation ?? null
            }));
          }
          continue;
        }

        // If this is a deleted record and we're skipping deletes, continue
        if (skipDeletes && record.deletedAt) {
          continue;
        }

        if (record.deletedAt) {
          if (!force) {
            operations.push(...this.deleteRecordSet(record.id));
          }
          continue;
        }

        // Delete associations and attachments for this record
        // We do not bother doing this is force is set as they are bulk-deleted anyway
        if (!force) {
          operations.push(...this.associations.toDeleteSetForRecord(record.id));
          operations.push(...this.attachments.toDeleteSetForRecord(record.id));
        }

        operations.push(...this.records.toInsertSet({
          ...record,
          projectId: id
        }));

        for (const attachment of record.attachments) {
          const existing = downloadedAttachments.find(a => a.recordId === record.id && a.url === attachment.url);

          operations.push(...this.attachments.toInsertSet({
            url: attachment.url,
            mimeType: attachment.mimeType,
            recordId: record.id,
            projectId: id,
            attributeId: attachment.attributeId,
            size: attachment.size,
            fileLocation: existing?.fileLocation ?? null
          }));
        }

        for (const association of record.sourceAssociations) {
          operations.push(...this.associations.toInsertSet([
            association.sourceId,
            association.targetId,
            association.attributeId,
            id
          ]));
        }

        await flushBuffer(SYNC_WRITE_SQL_BUFFER_SIZE);
      }
    }

    // Update our records pointer on the project
    if (lastRecord) {
      const projectUpdate = this.updateQueryBuilder('projects')
        .where('id = ?', id)
        .set('lastRecordId', lastRecord.id)
        .set('lastRecordTimestamp', lastRecord.timestamp);

      operations.push({
        statement: projectUpdate.toString(),
        values: []
      });
    }
    // Flush this and any remaining operations
    await flushBuffer();

    // If this was a force sync, the initial sync process would have ignored any deleted records
    // So we run another sync process now, but indicate that we can skip over applying deletes to
    // the database - this process is really just to ensure our lastRecordId and lastRecordTimestamp
    // fields are up to date
    if (force) {
      return this.syncRecords(id, transaction, signal, false, true);
    }

    // console.log('CONFLICTS', conflicts);
    return conflicts;
  }

  private async syncUsers(id: number, transaction: Transaction, signal: AbortSignal, force: boolean) {
    let [lastId, lastUpdatedAt] = await this.getLastIdAndTimestamp(id, 'users', transaction.query);
    const resource = new AppSyncResource<SyncUser>(id, 'users', lastId, lastUpdatedAt, SYNC_REQUEST_BATCH_SIZE, force, signal);
    const operations: capSQLiteSet[] = [];

    for await (const batch of resource) {
      for (const user of batch.result) {
        operations.push(...this.users.toInsertSet({
          ...user,
          projectId: id
        }));
      }
    }

    if (operations.length > 0 && !signal.aborted) {
      await transaction.executeSet(operations, SYNC_WRITE_SQL_BUFFER_SIZE);
    }

  }

  private async syncItems(id: number, transaction: Transaction, signal: AbortSignal, force: boolean, reconcile: boolean) {

    let [lastId, lastUpdatedAt] = await this.getLastIdAndTimestamp(id, 'items', transaction.query);
    const operations: capSQLiteSet[] = [];
    if (reconcile) {
      force = true;
      lastId = 0;
      lastUpdatedAt = 0;
      operations.push({
        statement: `DELETE FROM items WHERE projectId = ? AND collectionId IN (
          SELECT id FROM collections WHERE projectId = ?
        )`,
        values: [id, id]
      });
    }
    const resource = new AppSyncResource<SyncItem>(id, 'items', lastId, lastUpdatedAt, SYNC_REQUEST_BATCH_SIZE, force, signal);

    const flushBuffer = async (bufferSize = 0) => {
      if (operations.length > 0 && (operations.length > bufferSize)) {
        await transaction.executeSet(operations);
        operations.length = 0;
      }
    };

    // If this is a force sync, delete all the media items now
    if (force) {
      operations.push({
        statement: 'DELETE FROM mediaItems WHERE projectId = ? AND itemId IS NOT NULL',
        values: [
          id
        ]
      });
    }

    for await (const batch of resource) {
      for (const item of batch.result) {
        // If this isn't a force sync, delete any existing mediaItems
        if (!force) {
          operations.push({
            statement: 'DELETE FROM mediaItems WHERE projectId = ? AND itemId = ?',
            values: [
              id,
              item.id
            ]
          });
        }

        // Add the item

        operations.push(...this.items.toInsertSet({
          ...item,
          projectId: id
        }));
        // Flush it from our cache
        this.itemCache.get(item.collectionId)?.delete(item.key);
        for (const mediaItem of item.mediaItems) {
          operations.push(...this.mediaItems.toInsertSet({
            ...mediaItem,
            projectId: id,
            itemId: item.id,
            pageId: null
          }));
        }

        // If this item has an icon, download it now
        if (item.icon) {
          try {
            const response = await fetch(imageToImageProxy(item.icon));
            const data = await response.blob();
            await writeProjectFile(id, data, 'items', item.id);
          } catch (e) {
            console.warn(e);
          }
        }
        await flushBuffer(SYNC_WRITE_SQL_BUFFER_SIZE);
      }
    }
    await flushBuffer();
  }

  public async uploadRecords(id: number, signal: AbortSignal): Promise<Map<number, number>> {
    const updates = await this.records.getPendingUpdateIds(id);
    if (updates.length === 0) {
      return null;
    }

    const records: any[] = [];

    for (const recordId of updates) {

      const { syncError, treeSyncError, syncState, timestamp, geometryCenter, updatedAt, deletedAt, origSyncState, ...record } = await this.records.findById(recordId);
      record.data = await this.loadRecordData(recordId, record.formId);

      const attachments = await this.attachments.query({ recordId });

      for (const attachment of attachments) {

        try {
          const hasUrl = attachment.url?.startsWith('http');

          // If we have an attachment with a URL but a size of 0, that indicates it has been copied
          // from another record, so we need to upload it again
          if (hasUrl && attachment.size === 0) {
            attachment.url = await uploadAttachmentClone(id, attachment, signal);
            await this.updateAttachmentUrl(attachment.id, attachment.url);
          } else if (!hasUrl) {
            // If we don't have a URL, upload it
            const url = await uploadAttachment(id, attachment, signal);
            attachment.url = url;
            await this.updateAttachmentUrl(attachment.id, url);
          }

          if (!(attachment.size && attachment.mimeType)) {
            const { size, mimeType } = await fetchAttachmentMetadata(attachment.url, signal);
            attachment.size = size;
            attachment.mimeType = mimeType;
            await this.updateAttachmentProperties(attachment.id, size, mimeType);
          }
        } catch (e) {
          if (!signal.aborted) {
            await this.pendingError(recordId, "Upload attachment failed", {
              attachment,
              recordId
            });
            Sentry.captureException(e);
          }
          throw new ProjectUploadError('Upload attachment failed', 'attachment');
        }
      }

      const associates = await this.associations.query({ sourceId: recordId });

      records.push({
        ...record,
        attachments,
        deletedAt: syncState === CoreoRecordSyncStatus.PENDING_DELETE ? new Date().toISOString() : null,
        associates: associates.map(({ sourceId, targetId, attributeId }) => [sourceId, targetId, attributeId])
      });
    }

    const url = `/sync/${id}/records`;
    let result: Response;
    let response: { [id: number]: CoreoRecord };

    try {
      result = await CoreoAPI.instance.post(url, JSON.stringify({
        records
      }), { signal: signal, authentication: true });
      response = await result.json();
    } catch (e) {
      throw new ProjectUploadError(result?.statusText ?? 'Upload Error', 'network');
    }

    const output: Map<number, number> = new Map<number, number>();

    for (const uploaded of records) {
      const recordId = uploaded.id;
      const entry = response[recordId];

      if (result.ok) {
        if (!isRecordUploadSuccessResponse(entry)) {
          continue;
        }

        if (recordId !== entry.id) {
          output.set(recordId, entry.id);
        }

        if (entry.deletedAt) {
          await this.resolvePendingDelete(entry);
        } else {
          await this.resolvePendingUpdate(recordId, entry);
        }
      } else {
        if (!isRecordUploadSuccessResponse(entry)) {
          await this.pendingError(recordId, entry ?? 'Unknown Error', {
            record: uploaded,
            error: entry
          });
        } else {
          await clearRecordSyncError(recordId);
          await recomputeTreeSyncErrorsForRecord(recordId);
        }
      }
    }

    if (!result.ok) {
      const hasValidationFailure = records.some(uploaded => {
        const entry = response[uploaded.id];
        return !isRecordUploadSuccessResponse(entry) &&
          normaliseSyncError(entry).kind === 'validation';
      });
      throw new ProjectUploadError(
        result.statusText,
        hasValidationFailure ? 'validation' : 'server'
      );
    }
    return output;

  }

  public async deleteProjectSet(id: number): Promise<capSQLiteSet[]> {

    // const forms = await this.forms.query({ projectId: id });
    const operations: capSQLiteSet[] = [];

    operations.push(...this.associations.toDeleteSet(id));
    operations.push(...this.mediaItems.toDeleteSet(id));
    operations.push(...this.attributes.toDeleteSet(id));
    operations.push(...this.items.toDeleteSet(id));
    operations.push(...this.collections.toDeleteSet(id));
    operations.push(...this.pages.toDeleteSet(id));
    operations.push(...this.attachments.toDeleteSet(id));
    operations.push(...this.records.toDeleteSet(id));
    operations.push(...this.forms.toDeleteSet(id));
    operations.push(...this.states.toDeleteSet(id));
    operations.push(...this.users.toDeleteSet(id));
    operations.push(...this.projects.toDeleteSet(id));

    // for (const form of forms) {
    //   operations.push({
    //     statement: `DROP TABLE IF EXISTS records_${form.id}`,
    //     values: []
    //   });
    // }
    return operations;
  }

  public async deleteProject(id: number): Promise<void> {
    const operations = await this.deleteProjectSet(id);
    return SQLDatabase.instance.executeSet(operations);
  }

  public abortSync() {
    this.syncAbortController?.abort();
    this.syncAbortController = undefined;
  }

  public async syncProject(id: number, options: ProjectSyncOptions = { force: false }): Promise<CoreoApp> {

    const { force } = options;
    let app: CoreoApp;

    let transaction: Transaction;
    let check: ProjectSyncCheckResponse;
    /** Features patched in place during this sync, announced once the transaction commits. */
    let patchedFeatures: CoreoProjectFeatures | null = null;

    let configSyncRequired = force;
    let recordsSyncRequired = force;
    let reconcileItems = false;

    this.syncAbortController = new AbortController();
    const signal = this.syncAbortController.signal;
    signal.throwIfAborted = signal.throwIfAborted ?? (() => {
      if (signal.aborted) {
        throw new Error('Sync Aborted');
      }
    });

    const runStep = async (step: ProjectSyncStep, fn: () => Promise<void>) => {
      signal.throwIfAborted();
      // If we are only running one step, complete it
      if (options.step && options.step !== step) {
        return this.events.emit('syncProgress', step, ProjectSyncStepState.COMPLETE);
      }

      this.events.emit('syncProgress', step, ProjectSyncStepState.RUNNING);
      try {
        await fn();
        signal.throwIfAborted();
        this.events.emit('syncProgress', step, ProjectSyncStepState.COMPLETE);
      } catch (e) {
        this.events.emit('syncProgress', step, ProjectSyncStepState.ERROR);
        throw signal.aborted ? new ProjectUserAbortError(e.message) : e;
      }
    };

    const configStep = () => runStep(ProjectSyncStep.CONFIG, async () => {
      if (force) {
        const deleteOperations = await this.deleteProjectSet(id);
        signal.throwIfAborted();
        await transaction.executeSet(deleteOperations);
      } else {
        check = await this.syncCheck(id, signal, transaction.query);
        // Map features are patched in place; a change the synced config depends on reinstalls it.
        const featuresChange = await this.checkProjectFeatures(id, check?.features, transaction.query);
        if (featuresChange === 'config') {
          check.project = Math.max(check.project, 1);
        } else if (featuresChange === 'patch' && !check.project) {
          await transaction.executeSet(this.projectFeaturesSet(id, check.features));
          patchedFeatures = check.features;
        }
        configSyncRequired ||= check.project > 0;
        recordsSyncRequired ||= check.records > 0;
        const [, localItemsLastUpdated] = await this.getLastIdAndTimestamp(
          id,
          'items',
          transaction.query
        );
        const collectionItemsCount = await transaction.query<{ n: number }>({
          statement: 'SELECT COUNT(1) AS n FROM items WHERE projectId = ?',
          values: [id]
        });
        reconcileItems = (check?.itemsLastUpdated !== localItemsLastUpdated) || (collectionItemsCount[0]?.n !== check?.items);
      }

      if (force || check?.project) {
        signal.throwIfAborted();
        app = await this.installProject(id, transaction, signal);

        signal.throwIfAborted();
        await this.cacheProjectConfig(app);
        signal.throwIfAborted();
        await FileSystemService.instance.initProjectFilesystem(id);
        signal.throwIfAborted();
        await storeProjectIcon(id, app.icon);
        signal.throwIfAborted();
        await this.syncItems(id, transaction, signal, force, reconcileItems);
      }

    });

    const usersStep = () => runStep(ProjectSyncStep.USERS, async () => {
      if (force || check?.records || check?.project) {
        await this.syncUsers(id, transaction, signal, force);
      }
    });

    const recordsStep = () => runStep(ProjectSyncStep.RECORDS, async () => {
      if (force || check?.records || check?.project) {
        await this.syncRecords(id, transaction, signal, force);
      }
    });

    const uploadStep = () => runStep(ProjectSyncStep.UPLOAD, async () => {
      const recordsIdsMap = await this.uploadRecords(id, signal);
      if (recordsIdsMap !== null) {
        recordsSyncRequired = true;
        for (let [oldId, newId] of recordsIdsMap) {
          this.events.emit('syncRecordIdUpdate', oldId, newId);
        }
      }
    });

    try {
      this.events.emit('syncStart', id, force, options.showIssuesLink === true);
      transaction = await SQLDatabase.instance.transaction();

      await configStep();
      await usersStep();
      await recordsStep();

      await transaction?.commit();
      transaction = null;

      if (patchedFeatures) {
        this.events.emit('projectFeaturesUpdated', id, patchedFeatures);
      }

      await uploadStep();

      if (recordsSyncRequired || configSyncRequired) {
        this.events.emit('syncDidUpdate', id, recordsSyncRequired, configSyncRequired);
      }

      return app;
    } catch (e) {
      const isAbort = e.name === "AbortError" || e instanceof ProjectUserAbortError;

      if (!isAbort) {
        Sentry.captureException(e);
      }

      // let error: AppDatabaseSyncError = AppDatabaseSyncError.UNKNOWN;
      let error: ProjectSyncError = e;
      if (isAbort) {
        error = new ProjectUserAbortError('Sync Aborted');
      } else if (e instanceof ProjectFreeTrialExpiredError) {
        error = e
      } else if (e instanceof ProjectAccessDeniedError) {
        error = e
      } else if (e instanceof ProjectUploadError) {
        error = e
      }

      this.events.emit('syncError', error);

      if (transaction) {
        transaction.rollback();
      } else {
        // If we got here then the transaction was already commited.
        // That probably means the error happened in the upload step
        // We may still have applied some config changes that need to be
        const isProjectUploadError = error instanceof ProjectUploadError;
        if (recordsSyncRequired || configSyncRequired || isProjectUploadError) {
          this.events.emit('syncDidUpdate', id, recordsSyncRequired, configSyncRequired, isProjectUploadError);
        }
        throw new ProjectSyncPostCommitError(id, error);
      }

      throw error;
    } finally {
      this.events.emit('syncComplete', id, app);
      this.syncAbortController = undefined;
    }

  }
}

(window as any).db = AppDatabase.instance;
